import { useState } from 'react';
import type { PossibleRider, PossibleRiderAmendment } from '../types/lifecycle';
import { RiderBadge } from './RiderBadge';
import { RiderFeedbackDialog } from './RiderFeedbackDialog';

interface RiderExplanationPanelProps {
  possibleRider: PossibleRider;
}

function listOrDash(values: string[]) {
  return values.length > 0 ? values.join(', ') : 'nenalezeno';
}

function humanLabel(value: string | null) {
  if (!value) return 'bez veřejného štítku';
  return value === 'prilepek' ? 'Přílepek' : 'Možný přílepek';
}

function AmendmentDetail({ possibleRider, amendment }: { possibleRider: PossibleRider; amendment: PossibleRiderAmendment }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const ai = amendment.ai_assessment;
  return (
    <article className="rider-panel__amendment">
      <div className="rider-panel__amendment-head">
        <h5>{amendment.document_id}</h5>
        {amendment.label ? <RiderBadge label={amendment.label} /> : <span className="badge badge--rider-none">{humanLabel(null)}</span>}
      </div>
      <dl className="rider-panel__facts">
        <dt>Hlavní tisk novelizuje</dt>
        <dd>{listOrDash(possibleRider.main_text_novelizes_acts)}</dd>
        <dt>Pozměňovací návrh novelizuje</dt>
        <dd>{listOrDash(amendment.heuristic.amendment_novelizes_acts)}</dd>
        <dt>Překryv zákonů</dt>
        <dd>{amendment.heuristic.acts_overlap_ratio === null ? 'nelze určit' : `${Math.round(amendment.heuristic.acts_overlap_ratio * 100)} %`}</dd>
        <dt>Nové citlivé oblasti</dt>
        <dd>
          {amendment.heuristic.critical_areas_new_in_amendment.length > 0 ? (
            <span className="detail-panel__chips">
              {amendment.heuristic.critical_areas_new_in_amendment.map((area) => <span key={area} className="chip"><code>{area}</code></span>)}
            </span>
          ) : 'nenalezeno'}
        </dd>
        <dt>Fáze výskytu</dt>
        <dd>{amendment.appears_in_phase || 'nezjištěno'}</dd>
        <dt>AI stanovisko</dt>
        <dd>
          <span className="rider-panel__ai-line">
            verdikt: <code>{ai.verdict || 'nezjištěno'}</code>, jistota: <code>{ai.confidence || 'nezjištěno'}</code>
          </span>
          {ai.explanation && <p>{ai.explanation}</p>}
        </dd>
      </dl>
      {amendment.label && (
        <button type="button" className="rider-panel__feedback" onClick={() => setDialogOpen(true)}>
          Nesouhlasím s klasifikací
        </button>
      )}
      {dialogOpen && (
        <RiderFeedbackDialog possibleRider={possibleRider} amendment={amendment} onClose={() => setDialogOpen(false)} />
      )}
    </article>
  );
}

export function RiderExplanationPanel({ possibleRider }: RiderExplanationPanelProps) {
  return (
    <section className="detail-panel__section rider-panel">
      <div className="rider-panel__title-row">
        <h4><span aria-hidden="true">●</span> Možné přílepkové riziko</h4>
        {possibleRider.label && <RiderBadge label={possibleRider.label} />}
      </div>
      <p>
        Štítek je orientační upozornění, ne právní stanovisko. Kombinuje textové signály z pozměňovacího návrhu
        s AI stanoviskem a má pomoci rychle najít případy vhodné k ruční kontrole.
      </p>
      <div className="rider-panel__list">
        {possibleRider.amendments.map((amendment) => (
          <AmendmentDetail key={amendment.document_id} possibleRider={possibleRider} amendment={amendment} />
        ))}
      </div>
    </section>
  );
}
